import { UIErrorCode } from '../../errors/constants'
import { PlatformError } from '../../errors/utils'
import { ApiClientV1 } from '../api'
import { globalEventBus } from '../events'
import { packageLoader } from './packages'
import {
  ComponentDefinition,
  ComponentLoadingState,
  ComponentMetadata,
} from './types'
import { pollUntilTrue } from './utils'

export class ComponentLoader {
  /**
   * Using a LoadingState (loaded, error, loading) cache for this (and PackageLoader) so
   * that loadComponent can be a synchronous method. If the component is loaded then everything
   * can instantly render, if not it receives a promise. The cache is used here (even though
   * the package is also cached) to ensure the same Promise instance is returned if the component
   * is loading (e.g. if packageState.promise.then(...) were returned each time the component
   * were requested that would be a new Promise instance) - this is necessary for Suspense to
   * be used in React.
   */
  private cache: {
    [packageName: string]: { [name: string]: ComponentLoadingState }
  } = {}

  registerComponent(
    packageName: string,
    name: string,
    component: ComponentDefinition,
    componentMetadata?: ComponentMetadata,
  ) {
    this.cache[packageName] = this.cache[packageName] ?? {}

    // If the component is already loaded, don't overwrite it with a potentially different version.
    if (this.cache[packageName][name]?.state === 'loaded') {
      return
    }

    // If the component was loading or hasn't been requested yet, it can be cached.
    // This will also handle recovering from an error state if it is registered after the package load resolves.
    this.cache[packageName][name] = {
      state: 'loaded',
      value: component,
      metadata: componentMetadata,
    }

    globalEventBus.emit({
      topic: 'horizon:component:registered:v1',
      payload: {
        package: packageName,
        component: name,
      },
    })
  }

  loadComponent(
    apiClient: ApiClientV1,
    packageName: string,
    name: string,
  ): ComponentLoadingState {
    // Initialize cache for package if needed
    this.cache[packageName] = this.cache[packageName] ?? {}

    // If component is already loaded, return it immediately
    if (this.cache[packageName][name]?.state === 'loaded') {
      globalEventBus.emit({
        topic: 'horizon:component:loaded:v1',
        payload: { package: packageName, component: name },
      })
      return this.cache[packageName][name]
    }

    // Get package state
    const packageState = packageLoader.load(apiClient, packageName)

    // Handle package loading states
    switch (packageState.state) {
      case 'loading':
      case 'loaded':
        // If not already loading, set up loading state
        if (!this.cache[packageName][name]) {
          this.cache[packageName][name] = {
            state: 'loading',
            promise: pollUntilTrue({
              pollFunction: () =>
                this.cache[packageName][name]?.state === 'loaded',
              errorMessage: `Component '${name}' not registered to package '${packageName}' after maximum retries`,
            })
              .then(() => this.loadComponent(apiClient, packageName, name))
              .catch(() => {
                const error = new PlatformError(
                  `The package '${packageName}' was loaded, but the requested component '${name}' is not registered to it.`,
                  UIErrorCode.COMPONENT_NOT_REGISTERED,
                )
                console.error(error)
                this.cache[packageName][name] = { state: 'error', error }
                return this.cache[packageName][name]
              }),
          }
        }
        return this.cache[packageName][name]

      case 'error':
        this.cache[packageName][name] = packageState
        return packageState
    }
  }

  /**
   * List all components that have been registered.
   *
   * @returns A list of all components in the registry.  When tags is supplied, it filters
   * by those tags only.
   */
  listComponents({ tags }: { tags?: string[] | null } = {}) {
    const componentList = Object.keys(this.cache)
      .map((packageName) => {
        return Object.keys(this.cache[packageName]).map((name) => {
          const component = this.cache[packageName][name]
          // Only include components that have been loaded
          if (component.state !== 'loaded') {
            return null
          }
          return {
            package: packageName,
            component: name,
            metadata: component.metadata,
          }
        })
      })
      .flat()

    if (!tags || tags.length === 0) {
      return componentList
    }

    return componentList.filter((component) => {
      const tagsSupported = component?.metadata?.supports

      if (!tagsSupported || tagsSupported.length === 0) {
        return true
      }
      return tagsSupported.some((tag) => tags.includes(tag))
    })
  }
}

export const componentLoader = new ComponentLoader()

let nextUniqueId = 1
export const getUniqueId = () => `platformid-${nextUniqueId++}`
